import React, { useState, useRef, useEffect } from 'react';
import styles from './CustomSelect.module.scss';
import { ChevronDown } from 'lucide-react';
import CheckIcon from '../../../assets/iconsTsx/CheckIcon.tsx';

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string; description?: string; variant?: 'default' | 'accent' }[];
  placeholder?: string;
  icon?: React.ReactNode;
  label?: string;
  error?: string;
  placement?: 'bottom' | 'top';
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Seleccionar',
  icon,
  label,
  error,
  placement = 'bottom'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const dropdownClassName = `${styles.dropdown} ${placement === 'top' ? styles.dropdownTop : ''}`;

  return (
    <div className={styles.container} ref={dropdownRef}>
      {label && (
        <label className={styles.label}>
          {icon && <span className={styles.labelIcon}>{icon}</span>}
          {label}
        </label>
      )}
      
      <div
        className={`${styles.select} ${isOpen ? styles.open : ''} ${error ? styles.error : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className={styles.selectedValue}>
          {selectedOption ? selectedOption.label : placeholder}
        </div>
        <ChevronDown 
          className={`${styles.chevron} ${isOpen ? styles.rotated : ''}`} 
          size={18} 
        />
      </div>
      {error && <span className={styles.errorText}>{error}</span>}

      {isOpen && (
        <div className={dropdownClassName}>
      {options.map((option) => (
        <div
          key={option.value}
          className={`${styles.option} ${option.variant === 'accent' ? styles.accent : ''} ${
            value === option.value ? styles.selected : ''
          }`}
          onClick={() => handleSelect(option.value)}
        >
          <div className={styles.optionContent}>
            <span className={styles.optionLabel}>{option.label}</span>
            {option.description && <span className={styles.optionDescription}>{option.description}</span>}
          </div>
          {value === option.value && (
            <div className={styles.checkmark}>
              <CheckIcon className={styles.checkIcon} />
            </div>
          )}
        </div>
      ))}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
